import {
  AiBrain03Icon,
  Attachment01Icon,
  BotIcon,
  Calendar03Icon,
  Clock01Icon,
  FlashIcon,
  FlowIcon,
  Flag01Icon,
  GoogleSheetIcon,
  HierarchyIcon,
  Link02Icon,
  Mail01Icon,
  MailOpen01Icon,
  MailSend01Icon,
  MessageAdd01Icon,
  MessageDelay01Icon,
  MessageDone01Icon,
  MessageQuestionIcon,
  Moon01Icon,
  Refresh01Icon,
  SparklesIcon,
  StepOverIcon,
  StickyNote01Icon,
  Tag01Icon,
  Tag02Icon,
  TiktokIcon,
  Timer02Icon,
  UserAdd01Icon,
  UserEdit01Icon,
  UserGroup02Icon,
  UserMinus01Icon,
  UserRemove02Icon,
  VolumeMute01Icon,
  WebhookIcon,
  ZapIcon,
} from '@hugeicons/core-free-icons';

import { WORKFLOW_CANVAS_ADD_STEP_KEYS } from './constants';

/** Base path for i18n keys in `automation.json` → `FLOW.STEP_DESCRIPTIONS.<SLUG>.{SHORT|LONG}` */
export const WORKFLOW_STEP_DESCRIPTION_I18N_BASE =
  'AUTOMATION.FLOW.STEP_DESCRIPTIONS';

/** Groups action types that share the same canvas title prefix (e.g. Assign To #1, #2). */
const WORKFLOW_ACTION_DISPLAY_GROUP = {
  send_message: 'SEND_MESSAGE',
  ask_question: 'ASK_QUESTION',
  assign_agent: 'ASSIGN_TO',
  assign_team: 'ASSIGN_TO',
  branch: 'BRANCH',
  update_contact_attribute: 'UPDATE_CONTACT_FIELD',
  update_contact_tag: 'UPDATE_CONTACT_TAG',
  open_conversation: 'OPEN_CONVERSATION',
  resolve_conversation: 'CLOSE_CONVERSATION',
};

export function getWorkflowActionDisplayGroup(actionName) {
  return WORKFLOW_ACTION_DISPLAY_GROUP[actionName] || 'DEFAULT';
}

/**
 * @param {Array<{ action_name: string }>} actions
 * @param {number} index
 */
export function getWorkflowActionOrdinal(actions, index) {
  const group = getWorkflowActionDisplayGroup(actions[index]?.action_name);
  let count = 0;
  for (let i = 0; i <= index; i += 1) {
    if (getWorkflowActionDisplayGroup(actions[i]?.action_name) === group) {
      count += 1;
    }
  }
  return count;
}

/**
 * @param {string} actionName
 * @param {Array<{ action_name: string }>} actions
 * @param {number} index
 * @param {(key: string) => string} t
 */
export function getWorkflowActionNodeTitle(actionName, actions, index, t) {
  const group = getWorkflowActionDisplayGroup(actionName);
  const ordinal = getWorkflowActionOrdinal(actions, index);
  const label = t(`AUTOMATION.FLOW.ADD_STEPS.LABELS.${group}`);
  return `${label} #${ordinal}`;
}

/**
 * Accent family per step → card border + focus ring (Tailwind n-*).
 * `accentKey` on each step selects from this palette.
 */
export const WORKFLOW_ACCENT_PALETTE = {
  slate: {
    cardBorderClass: 'border border-n-slate-8 dark:border-n-slate-7',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-slate-10',
  },
  teal: {
    cardBorderClass: 'border border-n-teal-9 dark:border-n-teal-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-teal-10',
  },
  blue: {
    cardBorderClass: 'border border-n-blue-9 dark:border-n-blue-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-blue-10',
  },
  green: {
    cardBorderClass: 'border border-n-green-9 dark:border-n-green-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-green-10',
  },
  amber: {
    cardBorderClass: 'border border-n-amber-9 dark:border-n-amber-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-amber-10',
  },
  iris: {
    cardBorderClass: 'border border-n-iris-9 dark:border-n-iris-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-iris-10',
  },
  cyan: {
    cardBorderClass: 'border border-n-cyan-9 dark:border-n-cyan-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-cyan-10',
  },
  ruby: {
    cardBorderClass: 'border border-n-ruby-9 dark:border-n-ruby-8',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-ruby-10',
  },
  brand: {
    cardBorderClass: 'border border-n-brand dark:border-n-brand',
    ringActiveClass: 'ring-1 ring-offset-0 ring-n-brand',
  },
};

/** Trigger card uses ruby accent (matches trigger header). */
export const WORKFLOW_TRIGGER_ACCENT_KEY = 'ruby';

function applyWorkflowAccent(step) {
  const key = step.accentKey || 'slate';
  const palette = WORKFLOW_ACCENT_PALETTE[key] || WORKFLOW_ACCENT_PALETTE.slate;
  return {
    ...step,
    cardBorderClass: palette.cardBorderClass,
    ringActiveClass: palette.ringActiveClass,
  };
}

/**
 * Visual theme for workflow canvas nodes (@hugeicons/core-free-icons + Tailwind n-*).
 * `descriptionSlug` maps to i18n keys under FLOW.STEP_DESCRIPTIONS (SHORT / LONG).
 * `accentKey` maps to WORKFLOW_ACCENT_PALETTE (card border + active ring).
 */
const DEFAULT_STEP = {
  hugeIcon: FlowIcon,
  descriptionSlug: 'DEFAULT',
  accentKey: 'slate',
  headerBarClass:
    'border-b border-n-weak bg-n-slate-3/40 dark:border-n-strong dark:bg-n-slate-4/25',
  iconClass: 'text-n-slate-11',
  lineClass: 'bg-n-slate-7 dark:bg-n-slate-6',
  plusButtonClass:
    'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
};

/** @type {Record<string, Partial<typeof DEFAULT_STEP>>} */
const WORKFLOW_ACTION_STEP_THEME = {
  send_message: {
    descriptionSlug: 'SEND_MESSAGE',
    accentKey: 'cyan',
    hugeIcon: MailSend01Icon,
    headerBarClass:
      'border-b border-n-cyan-7 bg-n-cyan-3/50 dark:border-n-cyan-8 dark:bg-n-cyan-4/30',
    iconClass: 'text-n-cyan-10',
    lineClass: 'bg-n-cyan-10 dark:bg-n-cyan-10',
    plusButtonClass: 'text-n-cyan-10 hover:opacity-80 dark:text-n-cyan-10',
  },
  ask_question: {
    descriptionSlug: 'ASK_QUESTION',
    accentKey: 'blue',
    hugeIcon: MessageQuestionIcon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/45 dark:border-n-blue-8 dark:bg-n-blue-4/25',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  assign_agent: {
    descriptionSlug: 'ASSIGN_TO',
    accentKey: 'green',
    hugeIcon: UserAdd01Icon,
    headerBarClass:
      'border-b border-n-green-7 bg-n-green-3/45 dark:border-n-green-8 dark:bg-n-green-4/25',
    iconClass: 'text-n-green-10',
    lineClass: 'bg-n-green-10 dark:bg-n-green-10',
    plusButtonClass: 'text-n-green-10 hover:opacity-80 dark:text-n-green-10',
  },
  assign_team: {
    descriptionSlug: 'ASSIGN_TO',
    accentKey: 'green',
    hugeIcon: UserGroup02Icon,
    headerBarClass:
      'border-b border-n-green-7 bg-n-green-3/45 dark:border-n-green-8 dark:bg-n-green-4/25',
    iconClass: 'text-n-green-10',
    lineClass: 'bg-n-green-10 dark:bg-n-green-10',
    plusButtonClass: 'text-n-green-10 hover:opacity-80 dark:text-n-green-10',
  },
  branch: {
    descriptionSlug: 'BRANCH',
    accentKey: 'amber',
    hugeIcon: HierarchyIcon,
    headerBarClass:
      'border-b border-n-amber-7 bg-n-amber-3/50 dark:border-n-amber-8 dark:bg-n-amber-4/25',
    iconClass: 'text-n-amber-11',
    lineClass: 'bg-n-amber-8 dark:bg-n-amber-9',
    plusButtonClass:
      'border-n-amber-9 text-n-amber-11 hover:bg-n-amber-3 dark:border-n-amber-8 dark:hover:bg-n-amber-4',
  },
  update_contact_attribute: {
    descriptionSlug: 'UPDATE_CONTACT_FIELD',
    accentKey: 'iris',
    hugeIcon: UserEdit01Icon,
    headerBarClass:
      'border-b border-n-iris-7 bg-n-iris-3/45 dark:border-n-iris-8 dark:bg-n-iris-4/25',
    iconClass: 'text-n-iris-11',
    lineClass: 'bg-n-iris-8 dark:bg-n-iris-9',
    plusButtonClass:
      'border-n-iris-9 text-n-iris-11 hover:bg-n-iris-3 dark:border-n-iris-8 dark:hover:bg-n-iris-4',
  },
  update_contact_tag: {
    descriptionSlug: 'UPDATE_CONTACT_TAG',
    accentKey: 'iris',
    hugeIcon: Tag01Icon,
    headerBarClass:
      'border-b border-n-iris-7 bg-n-iris-3/45 dark:border-n-iris-8 dark:bg-n-iris-4/25',
    iconClass: 'text-n-iris-11',
    lineClass: 'bg-n-iris-8 dark:bg-n-iris-9',
    plusButtonClass:
      'border-n-iris-9 text-n-iris-11 hover:bg-n-iris-3 dark:border-n-iris-8 dark:hover:bg-n-iris-4',
  },
  open_conversation: {
    descriptionSlug: 'OPEN_CONVERSATION',
    accentKey: 'cyan',
    hugeIcon: MessageAdd01Icon,
    headerBarClass:
      'border-b border-n-cyan-7 bg-n-cyan-3/45 dark:border-n-cyan-8 dark:bg-n-cyan-4/25',
    iconClass: 'text-n-cyan-11',
    lineClass: 'bg-n-cyan-8 dark:bg-n-cyan-9',
    plusButtonClass:
      'border-n-cyan-9 text-n-cyan-11 hover:bg-n-cyan-3 dark:border-n-cyan-8 dark:hover:bg-n-cyan-4',
  },
  resolve_conversation: {
    descriptionSlug: 'CLOSE_CONVERSATION',
    accentKey: 'cyan',
    hugeIcon: MessageDone01Icon,
    headerBarClass:
      'border-b border-n-cyan-7 bg-n-cyan-3/40 dark:border-n-cyan-8 dark:bg-n-cyan-4/25',
    iconClass: 'text-n-cyan-11',
    lineClass: 'bg-n-cyan-8 dark:bg-n-cyan-9',
    plusButtonClass:
      'border-n-cyan-9 text-n-cyan-11 hover:bg-n-cyan-3 dark:border-n-cyan-8 dark:hover:bg-n-cyan-4',
  },
  wait: {
    descriptionSlug: 'WAIT',
    accentKey: 'blue',
    hugeIcon: Clock01Icon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/35 dark:border-n-blue-8 dark:bg-n-blue-4/20',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  send_api_event: {
    descriptionSlug: 'SEND_API_EVENT',
    accentKey: 'blue',
    hugeIcon: SparklesIcon,
    headerBarClass:
      'border-b border-n-blue-8 bg-n-blue-3/40 dark:border-n-blue-9 dark:bg-n-blue-4/25',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-9 dark:bg-n-blue-10',
    plusButtonClass:
      'border-n-blue-10 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-9 dark:hover:bg-n-blue-4',
  },
  send_tiktok_lower_funnel_event: {
    descriptionSlug: 'SEND_TIKTOK_FUNNEL',
    accentKey: 'slate',
    hugeIcon: TiktokIcon,
    headerBarClass:
      'border-b border-n-slate-9 bg-n-slate-3/50 dark:border-n-slate-10 dark:bg-n-slate-5/30',
    iconClass: 'text-n-slate-12',
    lineClass: 'bg-n-slate-8 dark:bg-n-slate-7',
    plusButtonClass:
      'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
  },
  trigger_automation: {
    descriptionSlug: 'TRIGGER_WORKFLOW',
    accentKey: 'ruby',
    hugeIcon: ZapIcon,
    headerBarClass:
      'border-b border-n-ruby-7 bg-n-ruby-3/45 dark:border-n-ruby-8 dark:bg-n-ruby-4/25',
    iconClass: 'text-n-ruby-11',
    lineClass: 'bg-n-ruby-8 dark:bg-n-ruby-9',
    plusButtonClass:
      'border-n-ruby-9 text-n-ruby-11 hover:bg-n-ruby-3 dark:border-n-ruby-8 dark:hover:bg-n-ruby-4',
  },
  http_request: {
    descriptionSlug: 'HTTP_REQUEST',
    accentKey: 'amber',
    hugeIcon: Link02Icon,
    headerBarClass:
      'border-b border-n-amber-8 bg-n-amber-3/50 dark:border-n-amber-9 dark:bg-n-amber-4/25',
    iconClass: 'text-n-amber-11',
    lineClass: 'bg-n-amber-9 dark:bg-n-amber-10',
    plusButtonClass:
      'border-n-amber-10 text-n-amber-11 hover:bg-n-amber-3 dark:border-n-amber-9 dark:hover:bg-n-amber-4',
  },
  google_sheets_add_row: {
    descriptionSlug: 'GOOGLE_SHEETS_ROW',
    accentKey: 'green',
    hugeIcon: GoogleSheetIcon,
    headerBarClass:
      'border-b border-n-green-8 bg-n-green-3/50 dark:border-n-green-9 dark:bg-n-green-4/25',
    iconClass: 'text-n-green-11',
    lineClass: 'bg-n-green-9 dark:bg-n-green-10',
    plusButtonClass:
      'border-n-green-10 text-n-green-11 hover:bg-n-green-3 dark:border-n-green-9 dark:hover:bg-n-green-4',
  },
  update_lifecycle: {
    descriptionSlug: 'UPDATE_LIFECYCLE',
    accentKey: 'blue',
    hugeIcon: Refresh01Icon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/40 dark:border-n-blue-8 dark:bg-n-blue-4/20',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  ai_objective_legacy: {
    descriptionSlug: 'AI_OBJECTIVE',
    accentKey: 'slate',
    hugeIcon: AiBrain03Icon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-slate-3/35 dark:border-n-slate-8 dark:bg-n-slate-4/20',
    iconClass: 'text-n-slate-10',
    lineClass: 'bg-n-slate-7 dark:bg-n-slate-6',
    plusButtonClass:
      'border-n-slate-8 text-n-slate-10 hover:bg-n-slate-3 dark:border-n-slate-7 dark:hover:bg-n-slate-4',
  },
  add_label: {
    descriptionSlug: 'UPDATE_CONTACT_TAG',
    accentKey: 'iris',
    hugeIcon: Tag01Icon,
    headerBarClass:
      'border-b border-n-iris-7 bg-n-iris-3/40 dark:border-n-iris-8 dark:bg-n-iris-4/20',
    iconClass: 'text-n-iris-11',
    lineClass: 'bg-n-iris-8 dark:bg-n-iris-9',
    plusButtonClass:
      'border-n-iris-9 text-n-iris-11 hover:bg-n-iris-3 dark:border-n-iris-8 dark:hover:bg-n-iris-4',
  },
  remove_label: {
    descriptionSlug: 'UPDATE_CONTACT_TAG',
    accentKey: 'iris',
    hugeIcon: Tag02Icon,
    headerBarClass:
      'border-b border-n-iris-7 bg-n-iris-3/35 dark:border-n-iris-8 dark:bg-n-iris-4/20',
    iconClass: 'text-n-iris-11',
    lineClass: 'bg-n-iris-8 dark:bg-n-iris-9',
    plusButtonClass:
      'border-n-iris-9 text-n-iris-11 hover:bg-n-iris-3 dark:border-n-iris-8 dark:hover:bg-n-iris-4',
  },
  send_email_to_team: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'blue',
    hugeIcon: Mail01Icon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/40 dark:border-n-blue-8 dark:bg-n-blue-4/20',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  send_email_transcript: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'blue',
    hugeIcon: MailOpen01Icon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/35 dark:border-n-blue-8 dark:bg-n-blue-4/20',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  mute_conversation: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'slate',
    hugeIcon: VolumeMute01Icon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-slate-3/40 dark:border-n-slate-8 dark:bg-n-slate-4/20',
    iconClass: 'text-n-slate-11',
    lineClass: 'bg-n-slate-8 dark:bg-n-slate-7',
    plusButtonClass:
      'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
  },
  snooze_conversation: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'iris',
    hugeIcon: Moon01Icon,
    headerBarClass:
      'border-b border-n-iris-7 bg-n-iris-3/35 dark:border-n-iris-8 dark:bg-n-iris-4/20',
    iconClass: 'text-n-iris-11',
    lineClass: 'bg-n-iris-8 dark:bg-n-iris-9',
    plusButtonClass:
      'border-n-iris-9 text-n-iris-11 hover:bg-n-iris-3 dark:border-n-iris-8 dark:hover:bg-n-iris-4',
  },
  pending_conversation: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'amber',
    hugeIcon: MessageDelay01Icon,
    headerBarClass:
      'border-b border-n-amber-7 bg-n-amber-3/40 dark:border-n-amber-8 dark:bg-n-amber-4/20',
    iconClass: 'text-n-amber-11',
    lineClass: 'bg-n-amber-8 dark:bg-n-amber-9',
    plusButtonClass:
      'border-n-amber-9 text-n-amber-11 hover:bg-n-amber-3 dark:border-n-amber-8 dark:hover:bg-n-amber-4',
  },
  send_webhook_event: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'teal',
    hugeIcon: WebhookIcon,
    headerBarClass:
      'border-b border-n-teal-7 bg-n-teal-3/40 dark:border-n-teal-8 dark:bg-n-teal-4/20',
    iconClass: 'text-n-teal-11',
    lineClass: 'bg-n-teal-8 dark:bg-n-teal-9',
    plusButtonClass:
      'border-n-teal-9 text-n-teal-11 hover:bg-n-teal-3 dark:border-n-teal-8 dark:hover:bg-n-teal-4',
  },
  send_attachment: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'slate',
    hugeIcon: Attachment01Icon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-slate-3/40 dark:border-n-slate-8 dark:bg-n-slate-4/20',
    iconClass: 'text-n-slate-11',
    lineClass: 'bg-n-slate-8 dark:bg-n-slate-7',
    plusButtonClass:
      'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
  },
  add_private_note: {
    descriptionSlug: 'ADD_COMMENT',
    accentKey: 'amber',
    hugeIcon: StickyNote01Icon,
    headerBarClass:
      'border-b border-n-amber-7 bg-n-amber-3/35 dark:border-n-amber-8 dark:bg-n-amber-4/20',
    iconClass: 'text-n-amber-11',
    lineClass: 'bg-n-amber-8 dark:bg-n-amber-9',
    plusButtonClass:
      'border-n-amber-9 text-n-amber-11 hover:bg-n-amber-3 dark:border-n-amber-8 dark:hover:bg-n-amber-4',
  },
  change_priority: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'ruby',
    hugeIcon: Flag01Icon,
    headerBarClass:
      'border-b border-n-ruby-7 bg-n-ruby-3/35 dark:border-n-ruby-8 dark:bg-n-ruby-4/20',
    iconClass: 'text-n-ruby-11',
    lineClass: 'bg-n-ruby-8 dark:bg-n-ruby-9',
    plusButtonClass:
      'border-n-ruby-9 text-n-ruby-11 hover:bg-n-ruby-3 dark:border-n-ruby-8 dark:hover:bg-n-ruby-4',
  },
  add_sla: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'cyan',
    hugeIcon: Timer02Icon,
    headerBarClass:
      'border-b border-n-cyan-7 bg-n-cyan-3/40 dark:border-n-cyan-8 dark:bg-n-cyan-4/20',
    iconClass: 'text-n-cyan-11',
    lineClass: 'bg-n-cyan-8 dark:bg-n-cyan-9',
    plusButtonClass:
      'border-n-cyan-9 text-n-cyan-11 hover:bg-n-cyan-3 dark:border-n-cyan-8 dark:hover:bg-n-cyan-4',
  },
  remove_assigned_agent: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'slate',
    hugeIcon: UserMinus01Icon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-slate-3/40 dark:border-n-slate-8 dark:bg-n-slate-4/20',
    iconClass: 'text-n-slate-11',
    lineClass: 'bg-n-slate-8 dark:bg-n-slate-7',
    plusButtonClass:
      'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
  },
  remove_assigned_team: {
    descriptionSlug: 'DEFAULT',
    accentKey: 'slate',
    hugeIcon: UserRemove02Icon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-slate-3/40 dark:border-n-slate-8 dark:bg-n-slate-4/20',
    iconClass: 'text-n-slate-11',
    lineClass: 'bg-n-slate-8 dark:bg-n-slate-7',
    plusButtonClass:
      'border-n-slate-9 text-n-slate-11 hover:bg-n-slate-3 dark:border-n-slate-8 dark:hover:bg-n-slate-4',
  },
  jump_step: {
    descriptionSlug: 'JUMP_STEP',
    accentKey: 'teal',
    hugeIcon: StepOverIcon,
    headerBarClass:
      'border-b border-n-teal-7 bg-n-teal-3/35 dark:border-n-teal-8 dark:bg-n-teal-4/20',
    iconClass: 'text-n-teal-11',
    lineClass: 'bg-n-teal-8 dark:bg-n-teal-9',
    plusButtonClass:
      'border-n-teal-9 text-n-teal-11 hover:bg-n-teal-3 dark:border-n-teal-8 dark:hover:bg-n-teal-4',
  },
  date_time_window: {
    descriptionSlug: 'DATE_TIME',
    accentKey: 'blue',
    hugeIcon: Calendar03Icon,
    headerBarClass:
      'border-b border-n-blue-7 bg-n-blue-3/35 dark:border-n-blue-8 dark:bg-n-blue-4/20',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
  assign_ai_agent: {
    descriptionSlug: 'ASSIGN_AI_AGENT',
    accentKey: 'brand',
    hugeIcon: BotIcon,
    headerBarClass:
      'border-b border-n-slate-7 bg-n-brand/10 dark:border-n-slate-8 dark:bg-n-brand/15',
    iconClass: 'text-n-blue-11',
    lineClass: 'bg-n-blue-8 dark:bg-n-blue-9',
    plusButtonClass:
      'border-n-blue-9 text-n-blue-11 hover:bg-n-blue-3 dark:border-n-blue-8 dark:hover:bg-n-blue-4',
  },
};

/** Trigger node (ruby) — same Hugeicons stack as canvas actions */
export const WORKFLOW_TRIGGER_HUGEICON = FlashIcon;

/** Trigger node — i18n slug under FLOW.STEP_DESCRIPTIONS.TRIGGER */
export const WORKFLOW_TRIGGER_DESCRIPTION_SLUG = 'TRIGGER';

const DEFAULT_TRIGGER_STEP = {
  accentKey: 'ruby',
  hugeIcon: FlashIcon,
  headerBarClass:
    'border-b border-n-ruby-6 bg-n-ruby-3/40 dark:border-n-ruby-8 dark:bg-n-ruby-4/25',
  iconClass: 'text-n-ruby-10',
};

/** Selected trigger card border — matches icon (`text-n-ruby-10`, pink–red). */
const WORKFLOW_TRIGGER_CARD_BORDER_CLASS =
  'border border-n-ruby-10 dark:border-n-ruby-10';

const WORKFLOW_TRIGGER_LINE_CLASS = 'bg-n-ruby-10 dark:bg-n-ruby-10';

const WORKFLOW_TRIGGER_PLUS_BUTTON_CLASS =
  'border-n-ruby-10 text-n-ruby-10 hover:bg-n-ruby-3 dark:border-n-ruby-10 dark:hover:bg-n-ruby-4/30';

/**
 * @returns {typeof DEFAULT_TRIGGER_STEP & { cardBorderClass: string, ringActiveClass: string, lineClass: string, plusButtonClass: string }}
 */
export function getWorkflowTriggerStepVisual() {
  const palette = WORKFLOW_ACCENT_PALETTE.ruby;
  return {
    ...DEFAULT_TRIGGER_STEP,
    cardBorderClass: WORKFLOW_TRIGGER_CARD_BORDER_CLASS,
    ringActiveClass: palette.ringActiveClass,
    lineClass: WORKFLOW_TRIGGER_LINE_CLASS,
    plusButtonClass: WORKFLOW_TRIGGER_PLUS_BUTTON_CLASS,
  };
}

/**
 * @param {string} actionName
 * @returns {typeof DEFAULT_STEP}
 */
export function getWorkflowActionStepVisual(actionName) {
  const override = WORKFLOW_ACTION_STEP_THEME[actionName] || {};
  return applyWorkflowAccent({ ...DEFAULT_STEP, ...override });
}

/**
 * @param {string} actionName
 * @returns {{ short: string, long: string }} vue-i18n keys (paths under AUTOMATION)
 */
export function getWorkflowActionStepDescriptionI18nKeys(actionName) {
  const { descriptionSlug } = getWorkflowActionStepVisual(actionName);
  const base = WORKFLOW_STEP_DESCRIPTION_I18N_BASE;
  return {
    short: `${base}.${descriptionSlug}.SHORT`,
    long: `${base}.${descriptionSlug}.LONG`,
  };
}

/**
 * @returns {{ short: string, long: string }} vue-i18n keys for the workflow trigger card
 */
export function getWorkflowTriggerDescriptionI18nKeys() {
  const base = WORKFLOW_STEP_DESCRIPTION_I18N_BASE;
  return {
    short: `${base}.${WORKFLOW_TRIGGER_DESCRIPTION_SLUG}.SHORT`,
    long: `${base}.${WORKFLOW_TRIGGER_DESCRIPTION_SLUG}.LONG`,
  };
}

/**
 * @param {(key: string) => string} t
 */
export function buildWorkflowAddStepMenuItems(t) {
  return WORKFLOW_CANVAS_ADD_STEP_KEYS.map(actionKey => {
    const visual = getWorkflowActionStepVisual(actionKey);
    const descriptionKeys = getWorkflowActionStepDescriptionI18nKeys(actionKey);
    const group = getWorkflowActionDisplayGroup(actionKey);
    return {
      key: actionKey,
      title: t(`AUTOMATION.FLOW.ADD_STEPS.LABELS.${group}`),
      description: t(descriptionKeys.short),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
    };
  });
}

// Re-export for consumers that import menu keys from the theme module.
export { WORKFLOW_CANVAS_ADD_STEP_KEYS };
